import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

type Item = { path: string; icon: string; label: string };
type Grupo = { id: string; icon: string; label: string; items: Item[] };

const inicio: Item = { path: "/", icon: "📊", label: "Dashboard" };

const grupos: Grupo[] = [
  { id: "ventas", icon: "💰", label: "Ventas", items: [
    { path: "/facturacion", icon: "🧾", label: "Facturación" },
    { path: "/pedidos", icon: "🛒", label: "Pedidos" },
    { path: "/creditos", icon: "💳", label: "Créditos" },
    { path: "/clientes", icon: "👥", label: "Clientes" },
    { path: "/vendedores", icon: "🧑‍💼", label: "Vendedores" },
  ]},
  { id: "inventario", icon: "📦", label: "Inventario", items: [
    { path: "/productos", icon: "📦", label: "Productos" },
    { path: "/categorias", icon: "🏷️", label: "Categorías" },
  ]},
  { id: "web", icon: "🌐", label: "Web", items: [
    { path: "/marketing", icon: "📣", label: "Marketing" },
    { path: "/resenas", icon: "⭐", label: "Reseñas" },
    { path: "/banners", icon: "📢", label: "Banners" },
    { path: "/emojis", icon: "😀", label: "Iconos" },
  ]},
  { id: "admin", icon: "⚙️", label: "Administración", items: [
    { path: "/gastos", icon: "💸", label: "Gastos" },
    { path: "/reportes", icon: "📈", label: "Reportes" },
    { path: "/auditoria", icon: "🕵️", label: "Auditoría" },
    { path: "/descargar-app", icon: "📲", label: "Descargar App" },
  ]},
];

// Qué grupos están abiertos se recuerda en este navegador (si no se puede, se usan los valores por defecto)
const leerAbiertos = (): Record<string, boolean> => {
  try { return JSON.parse(localStorage.getItem("menu_grupos") || "{}") || {}; } catch { return {}; }
};
const guardarAbiertos = (v: Record<string, boolean>) => {
  try { localStorage.setItem("menu_grupos", JSON.stringify(v)); } catch { /* sin almacenamiento */ }
};

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [disco, setDisco] = useState<any>(null);
  const [sidebarAbierto, setSidebarAbierto] = useState(false);
  const [abiertos, setAbiertos] = useState<Record<string, boolean>>(leerAbiertos);
  const activo = (path: string) => path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
  const grupoAbierto = (g: Grupo) => abiertos[g.id] ?? g.items.some(i => activo(i.path)) ?? false;
  const alternarGrupo = (g: Grupo) => {
    const nuevo = { ...abiertos, [g.id]: !grupoAbierto(g) };
    setAbiertos(nuevo); guardarAbiertos(nuevo);
  };

  const enlace = (item: Item, sub = false) => (
    <Link key={item.path} to={item.path}
      style={{display:"flex",alignItems:"center",gap:"12px",padding:sub?"10px 16px 10px 28px":"12px 16px",borderRadius:"12px",textDecoration:"none",fontSize:sub?"13.5px":"14px",fontWeight:600,
        background:activo(item.path)?"rgba(255,255,255,0.15)":"transparent",
        color:activo(item.path)?"#fff":"rgba(255,255,255,0.6)"}}>
      <span>{item.icon}</span>{item.label}
    </Link>
  );

  useEffect(() => {
    fetch("/api/sistema/disco").then(r => r.json()).then(setDisco).catch(() => {});
  }, []);

  useEffect(() => {
    setSidebarAbierto(false);
  }, [location.pathname]);

  const cerrarSesion = () => { localStorage.removeItem("token"); navigate("/login"); };

  const Sidebar = () => (
    <aside style={{width:"240px",background:"#0D47A1",display:"flex",flexDirection:"column",height:"100%"}}>
      <div style={{padding:"24px",borderBottom:"1px solid rgba(255,255,255,0.1)"}}>
        <div style={{fontFamily:"Montserrat,sans-serif",fontWeight:900,fontSize:"18px",color:"#fff"}}>
          IMPORTACIONES <span style={{color:"#90CAF9"}}>B&Y</span>
        </div>
        <div style={{color:"rgba(255,255,255,0.4)",fontSize:"11px",marginTop:"4px"}}>Panel Admin</div>
      </div>
      <nav style={{flex:1,padding:"16px",display:"flex",flexDirection:"column",gap:"4px",overflowY:"auto",minHeight:0}}>
        {enlace(inicio)}
        {grupos.map(g => {
          const abierto = grupoAbierto(g);
          const tieneActivo = g.items.some(i => activo(i.path));
          return (
            <div key={g.id} style={{marginTop:"6px"}}>
              <button onClick={() => alternarGrupo(g)}
                style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px",background:"none",border:"none",cursor:"pointer",
                  color:tieneActivo?"#fff":"rgba(255,255,255,0.85)",fontSize:"12px",fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",fontFamily:"Nunito,sans-serif"}}>
                <span style={{display:"flex",alignItems:"center",gap:"10px"}}><span>{g.icon}</span>{g.label}</span>
                <span style={{fontSize:"10px",transition:"transform .2s",transform:abierto?"rotate(90deg)":"rotate(0deg)",opacity:.7}}>▶</span>
              </button>
              {abierto && <div style={{display:"flex",flexDirection:"column",gap:"2px"}}>{g.items.map(i => enlace(i, true))}</div>}
            </div>
          );
        })}
      </nav>
      {disco && (
        <div style={{padding:"16px",borderTop:"1px solid rgba(255,255,255,0.1)",flexShrink:0}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:"6px"}}>
            <span style={{color:"rgba(255,255,255,0.4)",fontSize:"11px"}}>Disco</span>
            <span style={{color:"rgba(255,255,255,0.6)",fontSize:"11px",fontWeight:700}}>{disco.usado_gb}GB / {disco.total_gb}GB</span>
          </div>
          <div style={{background:"rgba(255,255,255,0.1)",borderRadius:"8px",height:"8px",overflow:"hidden"}}>
            <div style={{height:"8px",borderRadius:"8px",width:`${disco.porcentaje}%`,
              background:disco.porcentaje<50?"linear-gradient(90deg,#22c55e,#86efac)":disco.porcentaje<75?"linear-gradient(90deg,#eab308,#fde047)":"linear-gradient(90deg,#ef4444,#f97316)",
              boxShadow:disco.porcentaje<50?"0 0 8px #22c55e":disco.porcentaje<75?"0 0 8px #eab308":"0 0 8px #ef4444"}}/>
          </div>
        </div>
      )}
      <div style={{padding:"16px",borderTop:"1px solid rgba(255,255,255,0.1)",flexShrink:0}}>
        <button onClick={cerrarSesion}
          style={{width:"100%",background:"none",border:"none",color:"rgba(255,255,255,0.6)",fontSize:"14px",fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:"12px",padding:"12px 16px",borderRadius:"12px",fontFamily:"Nunito,sans-serif"}}>
          🚪 Cerrar sesion
        </button>
      </div>
    </aside>
  );

  return (
    <div style={{display:"flex",height:"100vh",background:"#f3f4f6",fontFamily:"Nunito,sans-serif"}}>
      <style>{`
        @media (max-width: 768px) {
          .admin-sidebar-desktop { display: none !important; }
          .admin-sidebar-mobile { display: flex !important; }
        }
        @media (min-width: 769px) {
          .admin-sidebar-desktop { display: flex !important; }
          .admin-sidebar-mobile { display: none !important; }
          .admin-sidebar-overlay { display: none !important; }
        }
      `}</style>

      <div className="admin-sidebar-desktop" style={{flexDirection:"column",flexShrink:0}}>
        <Sidebar />
      </div>

      {sidebarAbierto && (
        <div className="admin-sidebar-overlay" onClick={() => setSidebarAbierto(false)}
          style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200}}/>
      )}
      <div className="admin-sidebar-mobile" style={{display:"none",position:"fixed",top:0,left:0,bottom:0,zIndex:201,transform:sidebarAbierto?"translateX(0)":"translateX(-100%)",transition:"transform .3s ease",width:"240px",flexDirection:"column"}}>
        <Sidebar />
      </div>

      <main style={{flex:1,overflow:"auto",display:"flex",flexDirection:"column",minWidth:0}}>
        <header style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
            <button className="admin-sidebar-mobile" onClick={() => setSidebarAbierto(!sidebarAbierto)}
              style={{display:"none",background:"#0D47A1",border:"none",color:"#fff",width:"38px",height:"38px",borderRadius:"10px",cursor:"pointer",fontSize:"18px",alignItems:"center",justifyContent:"center"}}>
              ☰
            </button>
            <div style={{fontSize:"14px",color:"#6b7280"}}>Bienvenido, <strong style={{color:"#0D47A1"}}>{localStorage.getItem("nombre")||"Admin"}</strong></div>
          </div>
          <div style={{fontSize:"12px",color:"#9ca3af",display:"none"}} className="admin-sidebar-desktop">Importaciones B&Y — Panel Admin</div>
        </header>
        <div style={{padding:"20px",flex:1,overflowY:"auto"}}><Outlet /></div>
      </main>
    </div>
  );
}
